/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import { SessionStorage } from '../../services/SessionStorage';
import { ScopeQuestionGroupOptionalValue } from '../../types/survey';
import { UtrValueType } from '../../types/universalTracker';
import { hasNumericColumnType, isNumericValueType } from '../../utils/universalTracker';
import { BulkActionUtr } from './partials/BulkActionToolbar';

export interface HighlightQuestion {
  questionIndex: number;
  groupId: string;
  rowRef: React.RefObject<HTMLDivElement>;
}

export const useHighlightQuestion = ({
  surveyGroups,
}: {
  surveyGroups: ScopeQuestionGroupOptionalValue[];
}): Omit<HighlightQuestion, 'rowRef'> | undefined => {
  const sessionIndex = SessionStorage.getQuestionIndex();
  if (!sessionIndex) {
    return;
  }

  const questionIndex = parseInt(sessionIndex);
  let total = 0;
  for (let [ix, surveyGroup] of surveyGroups.entries()) {
    const totalQuestionCount = total + getTotalQuestionCount(surveyGroup);

    const isHighlightedInGroup = questionIndex >= total && questionIndex < totalQuestionCount;
    if (isHighlightedInGroup) {
      return {
        questionIndex,
        groupId: getQuestionGroupId(ix),
      };
    }
    total = totalQuestionCount;
  }

  return;
};

export const getQuestionGroupId = (index: number) => `questionGroup-${index}`;

export const getTotalQuestionCount = (surveyGroup: ScopeQuestionGroupOptionalValue) => {
  return surveyGroup.subGroups
    ? surveyGroup.subGroups.reduce((acc, subGroup) => {
        return subGroup.list ? acc + subGroup.list.length : acc;
      }, 0)
    : surveyGroup.list.length;
};
export const getQuestionSubGroupId = (groupIndex: number, subGroupIndex: number | undefined) =>
  `questionSubGroup-${groupIndex}-${subGroupIndex}`;

export const getNumericSelectedQuestions = (selectedQuestions: BulkActionUtr[] = []) => {
  return selectedQuestions.filter((question) => {
    switch (question.valueType) {
      case UtrValueType.Table:
        return hasNumericColumnType(question);
      default:
        return isNumericValueType(question.valueType as UtrValueType);
    }
  });
};
